import { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowRight, CheckCircle, Calendar, Mail, Clock, Star, Loader2 } from "lucide-react";
import { CALCOM_DEMO_URL } from "../config/demo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Reusable field components ──────────────────────────── */
function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold uppercase tracking-widest text-[#8B8BA7]">
        {label}{required && <span className="text-[#71DED6] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  "w-full bg-[#111118] border border-[#1E1E2E] rounded-xl px-4 py-3 text-[#F4F4F8] text-sm placeholder-[#3A3A5A] outline-none transition-all duration-200 focus:border-[#71DED6] focus:ring-2 focus:ring-[#71DED6]/20";

/* ── Jake M. testimonial ────────────────────────────────── */
const JAKE = {
  quote: "I booked 3 jobs in the first week that I know I would have lost before. The follow-up alone paid for itself.",
  name: "Jake M.",
  business: "Jake's Exterior Services",
  img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
};

export default function ContactPage() {
  usePageTitle("Contact | CleanBid.ai");

  const [form, setForm] = useState({
    name: "", business: "", email: "", phone: "", message: "", website: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.business.trim()) e.business = "Business name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Message is required";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return; // honeypot
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (submitting) return; // prevent duplicate while in-flight

    setSubmitting(true);
    setApiError("");
    setErrors({});

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setSubmitted(true);
      } else if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setApiError(data.message || "Please wait a moment before submitting again.");
      } else if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        setErrors(data.errors || {});
      } else {
        const data = await res.json().catch(() => ({}));
        setApiError(data.message || "Something went wrong. Please try again or email us at info@cleanbid.ai");
      }
    } catch {
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A0F] pt-20 min-h-screen" data-testid="contact-page">

      {/* ── Page Header ── */}
      <section className="py-16 md:py-20 border-b border-[#1E1E2E]">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#71DED6]">
            Get in touch
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#F4F4F8] mt-3 mb-4">
            We'd love to hear from you.
          </h1>
          <p className="text-lg text-[#8B8BA7] max-w-xl leading-relaxed">
            Whether you have a question about pricing, a partnership idea, or just want to learn more — we're real people and we respond fast.
          </p>
        </div>
      </section>

      {/* ── Two-column body ── */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">

            {/* ── LEFT: Form ── */}
            <div className="lg:col-span-3">
              {submitted ? (
                /* Success state */
                <div
                  className="bg-[#111118] border border-[#71DED6]/20 rounded-3xl p-10 text-center"
                  data-testid="contact-success-state"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#71DED6]/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-7 h-7 text-[#71DED6]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#F4F4F8] mb-2">Message sent.</h2>
                  <p className="text-[#8B8BA7] mb-8 leading-relaxed">
                    We'll get back to you within 1 business day.
                  </p>
                  <a
                    href={CALCOM_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-success-demo-link"
                    className="text-sm text-[#71DED6] hover:underline flex items-center justify-center gap-1.5"
                  >
                    Book a demo instead
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              ) : (
                /* Form */
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set("website")}
                    tabIndex={-1}
                    aria-hidden="true"
                    style={{ display: "none" }}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Name" required>
                      <input
                        type="text"
                        placeholder="Jane Smith"
                        value={form.name}
                        onChange={set("name")}
                        data-testid="contact-name-input"
                        className={`${inputClass} ${errors.name ? "border-red-500/60 focus:border-red-500" : ""}`}
                      />
                      {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                    </Field>
                    <Field label="Business name" required>
                      <input
                        type="text"
                        placeholder="Jane's Cleaning Co."
                        value={form.business}
                        onChange={set("business")}
                        data-testid="contact-business-input"
                        className={`${inputClass} ${errors.business ? "border-red-500/60 focus:border-red-500" : ""}`}
                      />
                      {errors.business && <p className="text-xs text-red-400 mt-1">{errors.business}</p>}
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Email" required>
                      <input
                        type="email"
                        placeholder="jane@cleaningco.com"
                        value={form.email}
                        onChange={set("email")}
                        data-testid="contact-email-input"
                        className={`${inputClass} ${errors.email ? "border-red-500/60 focus:border-red-500" : ""}`}
                      />
                      {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                    </Field>
                    <Field label="Phone">
                      <input
                        type="tel"
                        placeholder="(555) 000-0000"
                        value={form.phone}
                        onChange={set("phone")}
                        data-testid="contact-phone-input"
                        className={inputClass}
                      />
                    </Field>
                  </div>

                  <Field label="Message" required>
                    <textarea
                      rows={4}
                      placeholder="Tell us a bit about your business and what you'd like to know..."
                      value={form.message}
                      onChange={set("message")}
                      data-testid="contact-message-input"
                      className={`${inputClass} resize-none ${errors.message ? "border-red-500/60 focus:border-red-500" : ""}`}
                    />
                    {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message}</p>}
                  </Field>

                  {apiError && (
                    <div
                      role="alert"
                      data-testid="contact-api-error"
                      className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-400"
                    >
                      {apiError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="w-full bg-[#71DED6] text-[#10151D] font-semibold py-3.5 rounded-xl text-sm hover:scale-[1.01] hover:shadow-[0_0_20px_rgba(113,222,214,0.3)] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* ── RIGHT: Info ── */}
            <div className="lg:col-span-2 space-y-8">

              {/* Book a call */}
              <div className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6 space-y-3">
                <div className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 text-[#71DED6]" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#8B8BA7]">
                    Rather book a call?
                  </span>
                </div>
                <p className="text-sm text-[#8B8BA7] leading-relaxed">
                  Skip the form — get a live 20-minute walk-through of CleanBid tailored to your business.
                </p>
                <a
                  href={CALCOM_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-demo-link"
                  className="inline-flex items-center gap-2 bg-[#71DED6] text-[#10151D] font-semibold px-5 py-2.5 rounded-full text-sm hover:scale-[1.02] transition-transform duration-200"
                >
                  Book a Demo
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#71DED6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-4 h-4 text-[#71DED6]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#8B8BA7] mb-1">
                    Email us directly
                  </p>
                  <a
                    href="mailto:info@cleanbid.ai"
                    data-testid="contact-email-link"
                    className="text-[#F4F4F8] text-sm hover:text-[#71DED6] transition-colors duration-200"
                  >
                    info@cleanbid.ai
                  </a>
                </div>
              </div>

              {/* Response time */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#71DED6]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-4 h-4 text-[#71DED6]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#8B8BA7] mb-1">
                    Response time
                  </p>
                  <p className="text-[#F4F4F8] text-sm">
                    We respond to all messages within 1 business day.
                  </p>
                </div>
              </div>

              {/* Testimonial */}
              <div
                className="bg-[#111118] border border-[#1E1E2E] rounded-2xl p-6"
                data-testid="contact-testimonial"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-[#71DED6] text-[#71DED6]" />
                  ))}
                </div>
                <blockquote className="text-[#8B8BA7] text-sm leading-relaxed mb-5">
                  "{JAKE.quote}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <img
                    src={JAKE.img}
                    alt={JAKE.name}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-[#F4F4F8] text-sm font-semibold">{JAKE.name}</p>
                    <p className="text-[#5A5A7A] text-xs">{JAKE.business}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
